import store from '@/store';
import type {
  PermissionActionKey,
  PermissionItem,
  PermissionTreeNode
} from '@/types/auth';

export function rolePermission(roleList: RoleText[] = []): boolean {
  const payload = roleList;
  payload.push('ALL');
  return store.getters['auth/permit'](payload);
}

const permissionPriority: Record<PermissionActionKey, number> = {
  NONE: 0,
  VIEW: 1,
  EDIT: 2,
  CRUD: 3
};

function isLegacyPermissionItem(p: PermissionItem): p is {
  screenId: number;
  actionKey: PermissionActionKey;
} {
  return typeof (p as { screenId?: unknown }).screenId === 'number';
}

function isPermissionTreeRoots(permissions: PermissionItem[]): boolean {
  return permissions.length > 0 && !isLegacyPermissionItem(permissions[0]);
}

function findActionKeyByCodeInTree(
  nodes: PermissionTreeNode[] | undefined,
  targetCode: string,
  parentCode?: string
): PermissionActionKey | undefined {
  if (!nodes?.length) return undefined;
  for (const node of nodes) {
    const candidateCodes = new Set<string>([node.code]);
    if (
      parentCode &&
      !node.code.includes('-') &&
      !node.code.startsWith(`${parentCode}-`)
    ) {
      candidateCodes.add(`${parentCode}-${node.code}`);
    }
    if (candidateCodes.has(targetCode)) return node.actionKey;
    const nested = findActionKeyByCodeInTree(
      node.childs,
      targetCode,
      node.code
    );
    if (nested !== undefined) return nested;
  }
  return undefined;
}

function buildImplicitButtonPermissions(
  node: PermissionTreeNode
): PermissionTreeNode[] {
  const baseCode = node.code;
  if (!baseCode) return [];
  if (node.actionKey === 'CRUD') {
    return [
      { code: `${baseCode}-BTN_CREATE`, actionKey: 'CRUD', childs: [] },
      { code: `${baseCode}-BTN_EDIT`, actionKey: 'CRUD', childs: [] },
      { code: `${baseCode}-BTN_DELETE`, actionKey: 'CRUD', childs: [] }
    ];
  }
  if (node.actionKey === 'EDIT') {
    return [{ code: `${baseCode}-BTN_EDIT`, actionKey: 'EDIT', childs: [] }];
  }
  return [];
}

function mergeImplicitButtons(
  nodes: PermissionTreeNode[]
): PermissionTreeNode[] {
  return nodes.map((node) => {
    const children = mergeImplicitButtons(node.childs ?? []);
    const existingCodes = new Set(children.map((child) => child.code));
    const implicitChildren = buildImplicitButtonPermissions(node).filter(
      (child) => !existingCodes.has(child.code)
    );
    return {
      ...node,
      childs: [...children, ...implicitChildren]
    };
  });
}

const getPermissionAction = (screenId: number): PermissionActionKey => {
  const auth = store.getters['auth/auth'] as
    | { permissions?: PermissionItem[] }
    | undefined;
  const items = auth?.permissions;
  if (!items?.length || !isLegacyPermissionItem(items[0])) {
    return 'NONE';
  }
  const matched = (
    items as { screenId: number; actionKey: PermissionActionKey }[]
  ).find((item) => item.screenId === screenId);
  return matched?.actionKey ?? 'NONE';
};

type PermissionScreenNode = {
  id: number;
  code: string;
  childs?: Array<{ id: number; code: string }>;
};

const resolveScreenIdByCode = (screenCode: string): number | null => {
  const screens =
    (store.getters['rolePermission/screens'] as PermissionScreenNode[]) ?? [];
  for (const screen of screens) {
    if (screen.code === screenCode) return screen.id;
    const child = screen.childs?.find((item) => item.code === screenCode);
    if (child) return child.id;
  }
  return null;
};

export function hasScreenPermission(
  screenId: number,
  requiredAction: PermissionActionKey = 'VIEW'
): boolean {
  return (
    permissionPriority[getPermissionAction(screenId)] >=
    permissionPriority[requiredAction]
  );
}

export function hasScreenCodePermission(
  screenCode: string,
  requiredAction: PermissionActionKey = 'VIEW'
): boolean {
  const auth = store.getters['auth/auth'] as { permissions?: PermissionItem[] };
  const permissions = auth?.permissions;
  if (!permissions?.length) return false;

  if (isPermissionTreeRoots(permissions)) {
    const normalizedPermissions = mergeImplicitButtons(
      permissions as PermissionTreeNode[]
    );
    const action = findActionKeyByCodeInTree(normalizedPermissions, screenCode);
    if (action === undefined) return false;
    return permissionPriority[action] >= permissionPriority[requiredAction];
  }

  const screenId = resolveScreenIdByCode(screenCode);
  if (!screenId) {
    return false;
  }
  return hasScreenPermission(screenId, requiredAction);
}

export function matchPasswordRegex(password: string): boolean {
  const passwordRegex =
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])[a-zA-Z0-9.?/\-_]{8,100}$/;
  return passwordRegex.test(password);
}

export enum Role {
  ORDERING = 1,
  SHIPPING_FINTECH = 2,
  SHIPPING_SMABLO = 3,
  TECH = 4,
  VIEW = 5,
  SHIPADMIN = 6,
  ORDERMANAGER = 8,
  ALL = 9
}

export type RoleText =
  | 'ORDERING'
  | 'SHIPPING_FINTECH'
  | 'SHIPPING_SMABLO'
  | 'TECH'
  | 'VIEW'
  | 'SHIPADMIN'
  | 'ORDERMANAGER'
  | 'ALL';
